import styled, { keyframes } from 'styled-components'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

export const Card = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md};
  padding: ${SPACING.xl};
  border-bottom: 1px solid var(--color-border);
`

export const InterviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const InterviewTitle = styled.h3`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
`

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  flex-wrap: wrap;
`

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

// ─── Quote block ──────────────────────────────────────────────────────────────

export const QuoteBlock = styled.blockquote`
  margin: 0;
  padding: ${SPACING.xl};
  background: color-mix(in srgb, ${COLORS.purple500} 6%, transparent);
  border-bottom: 1px solid var(--color-border);
`

export const QuoteMark = styled.span`
  display: block;
  font-size: 2.5rem;
  line-height: 1;
  color: ${COLORS.purple500};
  font-family: Georgia, serif;
  margin-bottom: ${SPACING.xs};
  user-select: none;
`

export const QuoteText = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-style: italic;
  color: var(--color-text-primary);
  line-height: 1.65;
  margin: 0 0 ${SPACING.sm};
`

export const QuoteAttribution = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: ${COLORS.purple600};
  font-weight: ${FONT_WEIGHT.medium};
  margin: 0;
  text-align: right;
`

// ─── Sections ─────────────────────────────────────────────────────────────────

export const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
`

export const Section = styled.div`
  padding: ${SPACING.xl};

  & + & {
    border-top: 1px solid var(--color-border);
  }
`

export const SectionTitle = styled.h4`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.md};
`

export const InsightsTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.md};
`

export const UnmetNeedsTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.md};
`

// ─── Quick facts ──────────────────────────────────────────────────────────────

export const FactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const FactLine = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

// ─── List items ───────────────────────────────────────────────────────────────

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

export const ListItem = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

// ─── Loading / pending ────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

export const PendingState = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  padding: ${SPACING.xl};
  animation: ${pulse} 2s ease-in-out infinite;
`

// ─── Legacy fallback ─────────────────────────────────────────────────────────

export const LegacySummarySection = styled.div`
  padding: ${SPACING.xl};
`

export const LegacySummaryLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${SPACING.sm};
`

export const LegacySummaryText = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.65;
  margin: 0;
  white-space: pre-wrap;
`
